/**
 * Frontend client for the SEO aggregates data.
 *
 * The county / zip aggregates feed every SEO landing page on the
 * site and rarely change — once per ETL season.  Rather than
 * having every page fire a live request to api.pxnres.com on
 * mount (which produced two cascading failure modes during
 * react-snap prerender: cross-origin CORS blocks and burst
 * 429s), the build pipeline bakes the data into static JSON at
 * ``public/data/aggregates/`` via ``scripts/build-aggregates-cache.js``.
 *
 * CRA copies ``public/`` into ``build/`` automatically, so the
 * runtime fetches are same-origin to the deployed site (S3 +
 * CloudFront), bypassing CORS and the rate-limit envelope on the
 * upstream API.  The data freshness contract — once-per-ETL —
 * matches the build cadence exactly, so this is no fresher / no
 * staler than the live API for these read paths.
 *
 * File layout served at ``/data/aggregates/`` (the build emits
 * more than the SPA actually reads — see scripts/build-aggregates-
 * cache.js for the full set):
 *   county-{slug}.json — single-county detail (read here)
 *   zip-{zip}.json     — single-zip detail (read here)
 *
 * The ``taxYear`` parameter is preserved on the function
 * signatures for API compatibility but ignored — the build is
 * single-year and embeds whatever year ran last.
 */
import axios from "axios";

// Same-origin path served by S3/CloudFront (or webpack-dev-server
// in development; CRA serves ``public/`` from ``/`` at dev time).
const STATIC_BASE = "/data/aggregates";

// Module-level cache.  Keyed by URL so navigating between county
// and zip pages in the same county doesn't re-fetch.  Lifetime is
// the SPA session — CDN edge caching handles cross-session reuse.
const cache = new Map();

function cachedGet(url) {
  if (cache.has(url)) {
    return Promise.resolve(cache.get(url));
  }
  return axios.get(url).then((res) => {
    cache.set(url, res.data);
    return res.data;
  });
}

/**
 * Fetch the single-county aggregate for a slug.
 *
 *   const data = await fetchCountyStats("travis");
 *   data.median_appraised_current  // current-year median
 *   data.extras.cities             // top cities by parcel count
 */
export function fetchCountyStats(slug) {
  if (!slug) return Promise.reject(new Error("slug required"));
  return cachedGet(`${STATIC_BASE}/county-${encodeURIComponent(slug)}.json`);
}

/**
 * Fetch a single zip's stats.  Returns the zip record plus its
 * parent county's slug so the page can link back to the county.
 */
export function fetchZipStats(zipCode) {
  if (!zipCode) return Promise.reject(new Error("zipCode required"));
  return cachedGet(`${STATIC_BASE}/zip-${encodeURIComponent(zipCode)}.json`);
}

/**
 * Test seam — clears the in-memory cache.  Used by tests that
 * mock axios and want to start each test with a clean slate.
 */
export function _clearCache() {
  cache.clear();
}
